import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { formatDate } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Receipt,
  CurrencyDollar,
  CheckCircle,
  Clock,
  Warning,
  Eye,
  Download,
  CalendarBlank,
  FileText,
  ArrowsClockwise,
  Info,
} from '@phosphor-icons/react';
import api from '../services/api';
import { useAgency } from '../context/AgencyContext';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: Warning },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500', icon: Info },
};

const AgencyBillingHistoryPage = () => {
  const { currentAgency, isAzaiStaff } = useAgency();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  // Summary stats
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalBilled: 0,
    totalPaid: 0,
    pendingAmount: 0,
  });

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await api.get('/agencies/billing/invoices', { params });
      const invoiceList = response.data.invoices || [];
      setInvoices(invoiceList);

      // Calculate stats
      const totalBilled = invoiceList.reduce((sum, inv) => sum + (inv.total || 0), 0);
      const totalPaid = invoiceList
        .filter(inv => inv.status === 'paid')
        .reduce((sum, inv) => sum + (inv.total || 0), 0);
      const pendingAmount = invoiceList
        .filter(inv => inv.status === 'pending')
        .reduce((sum, inv) => sum + (inv.total || 0), 0);

      setStats({
        totalInvoices: invoiceList.length,
        totalBilled,
        totalPaid,
        pendingAmount,
      });
    } catch (error) {
      toast.error('Failed to load billing history');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await api.get(`/agencies/billing/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const isOverdue = (invoice) => {
    if (invoice.status !== 'pending') return false;
    const dueDate = new Date(invoice.due_date);
    return dueDate < new Date();
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="agency-billing-history-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Billing History
          </h1>
          <p className="text-slate-500 mt-1">
            View your invoices and payment history
            {currentAgency && ` for ${currentAgency.name}`}
          </p>
        </div>
        <Button variant="outline" onClick={loadInvoices}>
          <ArrowsClockwise size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FileText size={20} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Invoices</p>
                <p className="text-xl font-bold">{stats.totalInvoices}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CurrencyDollar size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Billed</p>
                <p className="text-xl font-bold">{formatCurrency(stats.totalBilled)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Paid</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(stats.totalPaid)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pending</p>
                <p className="text-xl font-bold text-amber-600">{formatCurrency(stats.pendingAmount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-0">
          <CardTitle className="text-lg">Invoices</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="py-12 text-center">
              <Receipt size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No invoices found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => {
                  const status = isOverdue(invoice) ? 'overdue' : invoice.status;
                  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-mono text-sm">{invoice.id}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(invoice.created_at)}</TableCell>
                      <TableCell>
                        <div className={isOverdue(invoice) ? 'text-red-600 font-medium' : ''}>
                          {formatDate(invoice.due_date)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(invoice.total)}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          <StatusIcon size={12} className="mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedInvoice(invoice); setViewOpen(true); }}
                            title="View details"
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPdf(invoice)}
                            title="Download PDF"
                          >
                            <Download size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Payment Information Card */}
      <Card className="border-slate-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Info size={24} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900">Payment Information</h3>
              <p className="text-sm text-blue-700 mt-1">
                Payments are processed via ACH transfer. For questions about billing, 
                please contact AZAI Billing at <strong>billing@azaibilling.com</strong> 
                or call <strong>(614) 555-0100</strong>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Details Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Invoice Number</p>
                  <p className="font-mono font-medium">{selectedInvoice.id}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={STATUS_CONFIG[selectedInvoice.status]?.color || 'bg-slate-100'}>
                    {STATUS_CONFIG[selectedInvoice.status]?.label || selectedInvoice.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Billing Period</p>
                  <p>{formatDate(selectedInvoice.period_start)} - {formatDate(selectedInvoice.period_end)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Due Date</p>
                  <p className={isOverdue(selectedInvoice) ? 'text-red-600 font-medium' : ''}>
                    {formatDate(selectedInvoice.due_date)}
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-slate-500 mb-2">Line Items</p>
                <div className="space-y-2">
                  {selectedInvoice.line_items?.map((item, idx) => (
                    <div key={item.description || `item-${idx}`} className="flex justify-between text-sm">
                      <span>{item.description}</span>
                      <span className="font-medium">{formatCurrency(item.total)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                </div>
                {selectedInvoice.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Tax</span>
                    <span>{formatCurrency(selectedInvoice.tax)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>{formatCurrency(selectedInvoice.total)}</span>
                </div>
              </div>

              {selectedInvoice.status === 'paid' && (
                <div className="border-t pt-4 bg-green-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={20} />
                    <span className="font-medium">Paid on {formatDate(selectedInvoice.paid_date)}</span>
                  </div>
                  {selectedInvoice.transaction_id && (
                    <p className="text-sm text-green-600 mt-1">
                      Reference: {selectedInvoice.transaction_id}
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleDownloadPdf(selectedInvoice)}
                >
                  <Download size={16} className="mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgencyBillingHistoryPage;
